import moment from "moment";
import React, { useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import "react-bootstrap-multiselect/css/bootstrap-multiselect.css";
import "react-dropdown-tree-select/dist/styles.css";
import { MdKeyboardArrowRight } from "react-icons/md";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import PageLoader from "../components/PageLoader";
import StatContainer from "../components/StatContainer";
import StockPriceDisplay from "../components/StockPriceDisplay";
import { Constants } from "../libs/Constants";
import { getList } from "../libs/dbFunctions";
import {
  calculateTotalTranche,
  getDefaultPeriodSelection,
} from "../libs/periodLib";
import {
  convertDate,
  convertUTCDateInTimeZone,
  formatCurrency,
  generateRandomRGBColor,
  numberWithCommas,
} from "../libs/utils";
import "./AdminDashboard.scss";
import BlackScholeWidget from "./BlackScholeWidget";
import LineChart from "./Charts/LineChart";
import PeopleWidget from "./PeopleWidget";
import PeriodFilter from "./PeriodFilter";

const monthsToShow = 23;
export default function AdminDashboard(props) {
  const [period, setPeriod] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [displayPeriod, setDisplayPeriod] = useState(null);
  const [hasPeriod, setHasPeriod] = useState(false);
  const [tranche, setTranche] = useState(0);
  const [expenses, setExpenses] = useState({
    totalFairValue: 0,
    totalExpenses: 0,
    totalModificationCharge: 0,
    avgCostPerDay: 0,
    avgCostPerMonth: 0,
    avgContributionElection: 0,
    totalShares: 0,
  });
  const [plan, setPlan] = useState(null);
  const [loadingExpense, setLoadingExpense] = useState(false);
  const [loadingCohortGraph, setLoadingCohortGraph] = useState(false);
  const [offerPeriodData, setOfferPeriodData] = useState([]);
  const [activeCohortTab, setActiveCohortTab] = useState(true);
  const [shareDetail, setShareDetail] = useState([]);
  const [stockPrices, setStockPrices] = useState([]);

  useEffect(() => {
    onLoad();
  }, [props.isAuthenticated, props.userObj]);

  useEffect(() => {
    if (period) {
      loadShareReserve(periods[periods.indexOf(period) - 1]);
    } else {
      loadEmptyShareReserve();
    }
    loadHistoricalStockPrices();
  }, [period]);

  useEffect(() => {
    function load() {
      if (displayPeriod && plan) {
        loadExpenseDetails();
      }
    }
    load();
  }, [displayPeriod, plan]);

  async function onLoad() {
    props.setPageLoading(true);
    try {
      await getPlan();
      //fetch the period rows from database
      const periodDetails = await getList(
        Constants.dbTypes.PERIOD,
        props.activeCompany
      );
      if (periodDetails && periodDetails.length > 0) {
        setPeriods(periodDetails);
        let periodResponse = getDefaultPeriodSelection(periodDetails);
        if (periodResponse) {
          setHasPeriod(true);
          setDisplayPeriod(periodResponse.currentPeriod);
          setPeriod(periodResponse.currentPeriod);
          setTranche(periodResponse.currentTranche);
        }
      }
      loadOfferingPeriod();
    } catch (e) {
      toast.error(e);
    }
    props.setPageLoading(false);
  }

  async function loadOfferingPeriod() {
    //get the offering period data for the company
    try {
      let offeringPeriods = await getList(
        Constants.dbTypes.OFFERING_PERIOD,
        props.activeCompany
      );
      if (offeringPeriods && offeringPeriods.length > 0) {
        let index = 0;
        for (let off of offeringPeriods) {
          let color = generateRandomRGBColor(true, index);
          index++;
          off.color = color;
        }
        setOfferPeriodData(offeringPeriods);
      }
    } catch (e) {
      toast.error(e);
    }
  }

  function loadEmptyShareReserve() {
    setShareDetail({
      sharesDispersedLast: 0,
      currentAvailableShares: 0,
      trendingShareBurnRate: 0,
      lifeTimeOfCurrentShares: 0,
    });
  }
  async function loadExpenseDetails() {
    setLoadingExpense(true);
    try {
      let params = {
        summaryType: Constants.SUMMARY_TYPE.EXPENSE,
        startdate: displayPeriod.periodStartdate,
        enddate: displayPeriod.periodEnddate,
      };
      let expenseDetails = await getList(
        Constants.dbTypes.SUMMARY,
        props.activeCompany,
        params
      );

      if (expenseDetails) setExpenses(expenseDetails);
    } catch (e) {
      toast.error(e);
    }
    setLoadingExpense(false);
  }

  async function getPlan() {
    try {
      let planDetail = await getList(
        Constants.dbTypes.PLAN_DETAILS,
        props.activeCompany
      );

      if (planDetail && planDetail.length > 0) {
        planDetail = planDetail[0];
        planDetail.totalTranche = calculateTotalTranche(planDetail);
        setPlan(planDetail);
      }
    } catch (e) {
      toast.error(e);
    }
  }

  async function loadShareReserve(lastPeriod) {
    setLoadingCohortGraph(true);
    try {
      if (lastPeriod) {
        let params = {
          summaryType: Constants.SUMMARY_TYPE.SHARE_RESERVE,
          period: lastPeriod,
        };
        let shareDetails = await getList(
          Constants.dbTypes.SUMMARY,
          props.activeCompany,
          params
        );
        if (shareDetails) setShareDetail(shareDetails);
        else loadEmptyShareReserve();
      } else {
        loadEmptyShareReserve();
      }
    } catch (e) {
      toast.error(e);
    }
    setLoadingCohortGraph(false);
  }

  // This function is used to display 2 column number grid
  function renderNumberGrid(arrayOfData, title) {
    return (
      <div className="pic-chart-box">
        {title && <h5 className="fw-bold">{title}</h5>}
        {arrayOfData.map((data, index) => {
          return (
            <p className={data.classes} key={index}>
              {data.title}:{" "}
              <span className={`font-h2 ${data?.classes}`}>{data.value}</span>
            </p>
          );
        })}
      </div>
    );
  }

  // This function is used to load stock prices
  async function loadHistoricalStockPrices() {
    if (!props.activeCompany.code) {
      console.log(
        "Company's stock symbol must be provided to query for stock price data."
      );
      return;
    }

    let end = moment.utc().startOf("month");
    let start = moment
      .utc()
      .startOf("month")
      .subtract(monthsToShow, "month");

    let params = {
      stockTickerSymbol: props.activeCompany.code,
      fromDate: start.format(Constants.DATEFORMAT.YYYYMMDD),
      toDate: end.add(1, "month").format(Constants.DATEFORMAT.YYYYMMDD), // get stock price data up to next month so we get the current month's data
      period: "month",
    };

    try {
      let stockPriceData = await getList(
        Constants.dbTypes.STOCK_PRICES,
        props.activeCompany,
        params
      );

      // need to compare historical stock price data to months we want to show, since we may be missing some data
      let monthlyClosingPrices = [];
      let currDateLabel = start;
      let nextStockPriceDataIdx = 0;
      for (let i = 0; i <= monthsToShow; i++) {
        // get month and year of label's date so we can compare to stock prices that we know about; then increment month
        let currMonth = currDateLabel.month();
        let currMonthYear = currDateLabel.year();
        currDateLabel = currDateLabel.add(1, "month");

        // if we still have stock price data to check then compare month and year of the next stock price to see if it's a match
        if (nextStockPriceDataIdx < stockPriceData.length) {
          let stockPrice = stockPriceData[nextStockPriceDataIdx];
          let stockPriceDate = moment(stockPrice.date);

          if (
            stockPriceDate.month() === currMonth &&
            stockPriceDate.year() === currMonthYear
          ) {
            // add closing or opening price to the graph if the month and year match (day is irrelevant in this context)
            monthlyClosingPrices.push(
              stockPrice.close ? stockPrice.close : stockPrice.open
            );
            nextStockPriceDataIdx++;
            continue;
          }
        }

        // stock price does not exist in historical data, so we'll populate this month with a 0.
        monthlyClosingPrices.push(null);
      }

      setStockPrices(monthlyClosingPrices);
    } catch (e) {
      toast.error(e);
    }
  }

  // This function is used to render purchase point graph
  function renderCohortPurchasePointGraph() {
    let filterOfferingData = [];
    let now = convertUTCDateInTimeZone(
      moment.utc().valueOf(),
      props.activeCompany.timezone
    );
    let startMonth = moment
      .utc()
      .startOf("month")
      .subtract(monthsToShow, "month");

    if (offerPeriodData && offerPeriodData.length > 0) {
      let endGraphPeriod = now.clone().startOf("month");
      for (let o of offerPeriodData) {
        if (
          convertUTCDateInTimeZone(
            o.offerStartdate,
            props.activeCompany.timezone
          ).isBetween(startMonth, endGraphPeriod) ||
          convertUTCDateInTimeZone(
            o.offerEnddate,
            props.activeCompany.timezone
          ).isBetween(startMonth, endGraphPeriod)
        ) {
          filterOfferingData.push({ ...o });
        }
        // This means that our graph is lying between complete period
        if (
          o.offerStartdate < startMonth.valueOf() &&
          o.offerEnddate > endGraphPeriod.valueOf()
        ) {
          filterOfferingData.push({ ...o });
        }
      }
    }
    let labels = [],
      start = convertUTCDateInTimeZone(
        moment.utc().valueOf(),
        props.activeCompany.timezone
      )
        .startOf("month")
        .subtract(monthsToShow, "month");
    for (let i = 0; i <= monthsToShow; i++) {
      labels.push(start.clone());
      start = start.add(1, "month");
    }
    let datasets = [];

    if (stockPrices && stockPrices.length > 0) {
      datasets.push({
        label: "Stock Price",
        data: stockPrices,
        fill: false,
        backgroundColor: "#4E729B",
        borderColor: "#4E729B",
        lineTension: 0,
        borderWidth: 1,
      });
    }
    let resets = [];
    for (let op of filterOfferingData) {
      let data = [];
      let offerStartDate = convertUTCDateInTimeZone(
        op.offerStartdate,
        props.activeCompany.timezone
      );
      let offerEndDate = convertUTCDateInTimeZone(
        op.offerEnddate,
        props.activeCompany.timezone
      );
      let resetDate = convertUTCDateInTimeZone(
        op.resetPeriod,
        props.activeCompany.timezone
      );
      if (
        op.resetPeriod &&
        !resets.includes(op.resetPeriod) &&
        resetDate.isAfter(startMonth)
      ) {
        resets.push({
          type: "line",
          mode: "vertical",
          scaleID: "x",
          value: resetDate.startOf("month").format("MMM YY"),
          borderColor: "red",
          borderWidth: 2,
          borderDash: [2, 2],
        });
      }
      for (let l of labels) {
        if (
          l.isBefore(offerStartDate.clone().startOf("month")) ||
          l.isAfter(offerEndDate) ||
          (op.resetPeriod && l.isAfter(resetDate.startOf("month")))
        ) {
          data.push(null);
        } else {
          data.push(op.purchasePrice);
        }
      }
      datasets.push({
        label: offerStartDate.format("MMM YY"),
        data: data,
        fill: false,
        lineTension: 0,
        backgroundColor: op.color,
        borderColor: op.color,
        borderWidth: 2,
      });
    }
    let data = {
      labels: labels.map((l) => l.format("MMM YY")),
      datasets: datasets,
    };
    let options = {
      elements: {
        point: {
          radius: 0,
        },
      },
      plugins: {
        annotation: {
          annotations: resets,
        },
        datalabels: {
          display: false,
          anchor: "end",
          align: "top",
          font: {
            color: "#423E60",
            weight: 600,
            style: "Montserrat",
          },
        },
        tooltip: {
          enabled: false,
        },
        legend: {
          display: false,
        },
      },
      maintainAspectRatio: true,
      scales: {
        x: {
          type: "time",
          time: {
            displayFormats: {
              month: "MMM YY",
            },
            source: "labels",
            parser: "MMM YY",
            unit: "month",
            min: labels[0].format("MMM YY"),
            max: labels[labels.length - 1].format("MMM YY"),
          },
          ticks: {
            fontSize: 11,
            fontFamily: "Montserrat",
            fontColor: "#43425D",
            major: {
              enabled: false,
              fontStyle: "bold",
            },
            callback: function(value, index, values) {
              values[index].major = false;
              if (labels[index]) {
                value = labels[index].format("MMM YY");
                if (
                  filterOfferingData &&
                  filterOfferingData.find(
                    (op) =>
                      convertUTCDateInTimeZone(
                        op.offerStartdate,
                        props.activeCompany.timezone
                      ).format("MMM YY") === value ||
                      convertUTCDateInTimeZone(
                        op.offerEnddate,
                        props.activeCompany.timezone
                      ).format("MMM YY") === value
                  )
                ) {
                  values[index].major = true;
                }
                return value;
              }
            },
          },
        },
        y: {
          title: {
            display: true,
            text: "$",
            fontSize: 14,
          },
          gridLines: {
            display: true,
            drawBorder: false,
          },
          ticks: {
            fontSize: 12,
            fontFamily: "Montserrat",
            fontColor: "#43425D",
          },
        },
      },
    };
    return (
      <div className="pic-chart-box">
        <Row>
          <Col>
            <h5 className="fw-bold">Offering Periods Purchase Points</h5>
          </Col>
          <Col>
            <div className="pull-right">
              <StockPriceDisplay
                label="Recent Price:"
                stockSymbol={props.activeCompany.code}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <LineChart data={data} options={options} />
          </Col>
        </Row>
      </div>
    );
  }
  // This function is used to render cohorts
  function renderCohorts() {
    let filterOfferingData = [];
    if (offerPeriodData) {
      if (activeCohortTab) {
        filterOfferingData = offerPeriodData.filter(
          (o) => !o.resetPeriod && o.offerEnddate >= moment.utc().valueOf()
        );
      } else {
        filterOfferingData = offerPeriodData.filter(
          (o) => o.resetPeriod || o.offerEnddate < moment.utc().valueOf()
        );
      }
    }
    return (
      <div className="pic-chart-box">
        <div className="d-flex align-content-center">
          <span className={`display-5 ${activeCohortTab ? "fw-bold" : ""}`}>
            <a
              className="cursor-pointer"
              onClick={() => setActiveCohortTab(true)}
            >
              Active Offering Periods
            </a>
          </span>
          <div className="ms-2 vla"></div>
          <span
            className={`ms-2 display-5 ${!activeCohortTab ? "fw-bold" : ""}`}
          >
            <a
              className="cursor-pointer"
              onClick={() => setActiveCohortTab(false)}
            >
              Past Offering Periods
            </a>
          </span>
        </div>
        <div>
          <table className="w-100 cohort-table">
            <thead>
              <tr>
                <th>Start Date</th>
                {!activeCohortTab && <th>End Date</th>}
                <th>Employee Count</th>
                <th>Purchase Price</th>
              </tr>
            </thead>
            <tbody>
              {!filterOfferingData ||
                (filterOfferingData.length == 0 && (
                  <tr className="text-center">
                    {!activeCohortTab && <td colSpan="4">No data</td>}
                    {activeCohortTab && <td colSpan="3">No data</td>}
                  </tr>
                ))}
              {filterOfferingData.map((data, index) => {
                return (
                  <tr key={index}>
                    <td className="cohort-column">
                      <div
                        className="dot"
                        style={{ background: data.color }}
                      ></div>
                      {convertDate(
                        data.offerStartdate,
                        "MM/YY",
                        false,
                        props.activeCompany.timezone
                      )}
                    </td>
                    {!activeCohortTab && data.resetPeriod && (
                      <td>
                        {convertDate(
                          data.resetPeriod,
                          "MM/YY",
                          false,
                          props.activeCompany.timezone
                        )}
                      </td>
                    )}
                    {!activeCohortTab && !data.resetPeriod && (
                      <td>
                        {convertDate(
                          data.offerEnddate,
                          "MM/YY",
                          false,
                          props.activeCompany.timezone
                        )}
                      </td>
                    )}
                    <td>{numberWithCommas(data.userCount) || 0}</td>
                    <td className="d-flex justify-content-end align-items-center">
                      {formatCurrency(data.purchasePrice)}
                      <div className="icon-link">
                        <Link
                          to={`/expenseDashboard?op=${data.offerStartdate}`}
                        >
                          <MdKeyboardArrowRight></MdKeyboardArrowRight>
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // This function is used to render cool grapg
  function renderCoolGraph() {
    let shareReserveData = [
      {
        title: "Shares Dispersed Last Period",
        value: numberWithCommas(shareDetail.sharesDispersedLast || 0),
      },
      {
        title: "Current Available Shares",
        value: numberWithCommas(shareDetail.currentAvailableShares || 0),
      },
      {
        title: "Trending Share Burn Rate",
        value:
          numberWithCommas(shareDetail.trendingShareBurnRate || 0) +
          " per/month",
      },
      {
        title: "Lifetime of Current Shares",
        value:
          numberWithCommas(shareDetail.lifeTimeOfCurrentShares || 0) + " years",
        classes: "fw-bold",
      },
    ];
    return (
      <PageLoader isActive={loadingCohortGraph} individual={true}>
        <StatContainer title="Overview">
          <Row className="mb-2">
            <Col>{renderCohortPurchasePointGraph()}</Col>
          </Row>
          <Row>
            <Col sm={6}>
              {renderNumberGrid(shareReserveData, "Share Reserve")}
            </Col>
            <Col sm={6}>{renderCohorts()}</Col>
          </Row>
        </StatContainer>
      </PageLoader>
    );
  }
  function renderPeople() {
    return (
      <PeopleWidget {...props} period={period} periods={periods}></PeopleWidget>
    );
  }

  function renderBlackScholes() {
    return (
      <BlackScholeWidget
        {...props}
        period={period}
        tranche={tranche}
      ></BlackScholeWidget>
    );
  }
  function renderExpenseOverview() {
    return (
      <Row>
        <Col sm={6} className="exp-view">
          {hasPeriod && period && <h2> Expense Overview</h2>}
          {!hasPeriod && <h2>Expense Overview - No Period </h2>}
          <PageLoader isActive={loadingExpense} individual={true}>
            <div className="exp-view-box">
              <span>
                <div className="me-3 mb-3">
                  <PeriodFilter
                    timezone={props.activeCompany?.timezone}
                    periods={periods}
                    handlePeriodSelect={setDisplayPeriod}
                  ></PeriodFilter>
                </div>
              </span>
              <span>
                <div className="td-left">Total Fair Value:</div>
                <div className="td-right">
                  {formatCurrency(expenses.totalFairValue)}
                </div>
              </span>
              <span>
                <div className="td-left">Total Expense:</div>
                <div className="td-right">
                  {formatCurrency(expenses.totalExpenses)}
                </div>
              </span>
              <span>
                <div className="td-left">Total Modification Charge:</div>
                <div className="td-right">
                  {formatCurrency(expenses.totalModificationCharge)}
                </div>
              </span>
              <span>
                <div className="td-left">Average Cost/Day:</div>
                <div className="td-right">
                  {formatCurrency(expenses.avgCostPerDay)}
                </div>
              </span>
              <span>
                <div className="td-left">Average Cost/Month:</div>
                <div className="td-right">
                  {formatCurrency(expenses.avgCostPerMonth)}
                </div>
              </span>
              <span>
                <div className="td-left">Average Contribution Election:</div>
                <div className="td-right">
                  {formatCurrency(expenses.avgContributionElection)} /
                  {expenses.totalShares} shares
                </div>
              </span>
              {displayPeriod &&
                (displayPeriod.key === "currentPeriod" ||
                  displayPeriod.key === "lastPeriod") && (
                  <h3 className="mt-3">
                    <Link
                      className="cursor-pointer secondary"
                      to={`/expenseDashboard?period=${displayPeriod.periodStartdate}`}
                    >
                      View Full Expense Table
                    </Link>
                  </h3>
                )}
            </div>
          </PageLoader>
        </Col>
        <Col sm={6} className="exp-view">
          {renderBlackScholes()}
        </Col>
      </Row>
    );
  }
  return (
    <div className="container">
      <Card>
        <Card.Body>
          <Card.Title as="h2" className="mb10">
            Dashboard
          </Card.Title>
          {!props.pageLoading && (
            <div>
              <Row className="m-0">
                <Col sm={12} className="p-0">
                  {renderExpenseOverview()}
                </Col>
              </Row>
              {renderCoolGraph()}
              {renderPeople()}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
